import { dateAdd, dateFormat, dateFormatYMD, dateGetDate, dateGetYear, dateIsAfter, dateIsBefore, dateIsToday } from '@utils/dateFns';
import { fadeIn, hideElement, l, lLink, lc, logEvent, qs, qsa, showElement, storedItemCreate, storedItemRead } from '@utils/toolbox';
import { Base64 } from '@js/plugins/base64.js';
import { ConnectApi } from '@js/plugins/connectApi';
import { resultsRender } from '@components/resultsRender';
import { eventCategories, events } from '@js/utils/event';

const _defaults = {
	offset: 2,
	dates: {
		today: js_params.isoDates.today,
		from: js_params.isoDates.from,
		to: js_params.isoDates.to
	},
	dateRange: js_params.dateRange,
	weekdays: js_params.weekdays,
	errors: {
		notEnoughData: l('module.pricecutter.error.notEnoughData'),
		tripTooLong: l('module.pricecutter.error.tripTooLong'),
		departNotSet: l('searchBox.error.notSetDeparture', 'Vänligen fyll i en avreseort'),
		returnNotSet: l('searchBox.error.notSetDestination', 'Vänligen fyll i en destinationsort')
	}
};

export class PriceCutter {
	constructor (params) {
		const { searchData, dateSelector } = params;

		this.searchData = searchData;
		this.dateSelector = dateSelector;
		this.html = this.dateSelector.html.querySelector('.abPriceCutter');
		this.initialized = false;
		this.today = new Date(js_params.isoDates.today);
		this.lastSelectableDay = dateAdd(this.today, { days: 364 });

		this.outbounds = null;
		this.inbounds = null;
		this.returnTrip = null;
		this.departDates = [];
		this.returnDates = [];
		this.multiplier = null;
		this.baseline = null;
		this.selectedBound = false;

		this.datePricePopup = this.html.querySelector('.abPriceCutter__datePricePopup');

		this.searchParams = this.getSearchParameters();

		this.setHolidays();
		this.events();
	}

	load () {
		if (this.request) {
			this.request.abort();
		}

		let error = false;

		if (this.searchData.fromIata === 'undefined') {
			error = _defaults.errors.departNotSet;
		} else if (this.searchData.toIata === 'undefined') {
			error = _defaults.errors.returnNotSet;
		}

		if (error) {
			this.renderError(error);
			return;
		}

		this.doRequest();
	}

	doRequest () {
		const self = this;

		this.dateSelector.showLoader();

		if (this.selectedBound) {
			this.hideEstimatedPrice();
		}

		// add a slight delay so we're still able to see the pretty loader :)
		window.setTimeout(() => {
			try {
				if (!resultsRender.response?.meta?.lowprice) {
					this.dateSelector.hideLoader();
					return self.renderError(_defaults.errors.notEnoughData);
				}
				self.searchData.price = resultsRender.response.meta.lowprice;
				self.request = ConnectApi('PriceCutterAPI', 'getEstimatedPrices', Object.values(self.searchData)).then(response => self.parseResponse(response));
			} catch (error) {
				console.log(error);
			}
		}, 300);
	}

	parseResponse (response) {
		this.dateSelector.hideLoader();

		let error = false;

		if (!response) {
			error = _defaults.errors.notEnoughData;
		}

		if (response.errorCode || !response) {
			if (response.errorCode === 1) {
				error = _defaults.errors.notEnoughData;
			} else if (response.errorCode === 2) {
				error = _defaults.errors.tripTooLong;
			}

			if (error) {
				this.renderError(error);
				return;
			}

			// if (error || this.selectedBound === false)
			// 	return;
		} else {
			const callback = () => setTimeout(() => this.showEstimatedPrice(), 300);

			this.outbounds = response.outbounds;
			this.inbounds = response.inbounds;
			this.returnTrip = response.returnTrip;
			this.departDates = [];
			this.returnDates = [];
			this.multiplier = response.multiplier;
			this.baseline = response.baseline;

			for (let i = 0; i < this.outbounds.length; i++) {
				this.departDates.push(new Date(this.outbounds[i].departDate));

				if (this.returnTrip) {
					this.returnDates.push(new Date(this.inbounds[i].returnDate));
				}
			}

			this.setPriceRanges();
			this.render(callback);
		}

		this.initialized = true;
	}

	parseEstimates (response) {
		this.dateSelector.hideLoader();

		let error = false;

		if (response.errorCode) {
			if (response.errorCode === 1) {
				error = true;
				alert(_defaults.errors.notEnoughData);
			} else if (response.errorCode === 2) {
				error = true;
				alert(_defaults.errors.tripTooLong);
			}

			// if (error || this.selectedBound === false) {

			// }
		} else {
			this.outbounds = response.outbounds;
			this.inbounds = response.inbounds;
			this.returnTrip = response.returnTrip;
			this.departDates = [];
			this.returnDates = [];
			this.multiplier = response.multiplier;
			this.baseline = response.baseline;

			for (let i = 0; i < this.outbounds.length; i++) {
				this.departDates.push(new Date(this.outbounds[i].departDate));

				if (this.returnTrip) {
					this.returnDates.push(new Date(this.inbounds[i].returnDate));
				}
			}

			const prices = this.updatePriceRanges();

			this.updateBarHeight(prices, this.minPrice, this.maxPrice);

			window.setTimeout(() => {
				this.update(() => window.setTimeout(() => this.showEstimatedPrice(), 300));
			}, 300);
		}
	}

	render (cb = () => { }) {
		let html = this.renderGraph('depart');

		this.html.querySelector('.abPriceCutter__graph--depart .abPriceCutter__scroller').innerHTML = html;

		if (this.returnTrip) {
			this.html.classList.add('abPriceCutter--return');

			html = this.renderGraph('return');
			this.html.querySelector('.abPriceCutter__graph--return .abPriceCutter__scroller').innerHTML = html;
		} else {
			this.html.classList.remove('abPriceCutter--return');
		}


		qsa('.abPriceCutter').forEach((element) => {
			showElement(element);
			element.classList.add('fade-in');
		});

		this.animateBars();

		setTimeout(() => {
			this.scrollToActiveBars();
			this.updateMedianLines();
		}, 400);

		return cb();
	}

	renderError (error) {
		this.html.classList.add('abPriceCutter--hasError');
		fadeIn(this.html);
		this.html.querySelector('.abPriceCutter__error').innerHTML = `<span>${error}</span>`;
	}

	update (cb = () => { }) {
		let scrollerWidth = qs('.abPriceCutter__scroller').offsetWidth;
		const barWidth = qs('.abPriceCutter__bar').offsetWidth;

		if (!this.returnTrip) {
			scrollerWidth = qs('.abPriceCutter__day').offsetWidth * 23 + barWidth;
		}

		let html = this.renderGraph('depart', true);
		const scrollerDepart = this.html.querySelector('.abPriceCutter__graph--depart .abPriceCutter__scroller');
		scrollerDepart.innerHTML = html;
		scrollerDepart.scrollTo({ left: scrollerWidth / 2 - barWidth / 2 });

		if (this.returnTrip) {
			this.html.classList.add('abPriceCutter--return');

			html = this.renderGraph('return', true);
			const scrollerReturn = this.html.querySelector('.abPriceCutter__graph--return .abPriceCutter__scroller');
			scrollerReturn.innerHTML = html;
			scrollerReturn.scrollTo({ left: scrollerWidth / 2 - barWidth / 2 });
		} else {
			this.html.classList.remove('abPriceCutter--return');
		}

		window.setTimeout(() => this.animateBars(), 400);

		return cb();
	}

	renderGraph (bound, update = false) {
		let html = ``;
		const bounds = (bound === 'depart') ? this.outbounds : this.inbounds;
		let prevOutMonth = new Date(bounds[0].departDate).getMonth() - 1;

		for (let i = 0; i < bounds.length; i++) {
			const price = bounds[i].price;
			const date = (bound === 'depart') ? new Date(bounds[i].departDate) : new Date(bounds[i].returnDate);
			const currMonth = ((date.getMonth() + 1) > 12) ? 1 : (date.getMonth() + 1);

			if (currMonth != prevOutMonth) {
				if (i > 0) {
					html += `</div>`;
					/* close prev days wrapper */
					html += `</div>`;
					/* close prev month wrapper */
				}

				html += `<div class="abPriceCutter__month">`;

				html += this.renderMonth({
					year: dateGetYear(date),
					month: dateFormat(date, 'MMM')
				});

				html += `<div class="abPriceCutter__days">`;
			}

			if (!update) {
				html += this.renderDay({
					bound: bound,
					date: (bound === 'depart') ? bounds[i].departDate : bounds[i].returnDate,
					price: price
				});
			} else {
				html += this.renderDay({
					bound: bound,
					date: (bound === 'depart') ? bounds[i].departDate : bounds[i].returnDate,
					price: price
				}, true);
			}

			prevOutMonth = currMonth;
		}

		html += `
					</div>
				</div>
			`;

		return html;
	}

	renderMonth (obj) {
		return `<div class="abPriceCutter__monthstr">${obj.month} ${obj.year}</div>`;
	}

	renderDay (obj, update = false) {
		const dateObject = new Date(obj.date);
		const date = dateFormatYMD(dateObject);
		const dateLong = dateFormat(dateObject, 'd MMM yyyy');
		const percent = ((Math.ceil(((obj.price - this.minPrice) * 100) / ((this.maxPrice - this.minPrice) * 100) * 100)) * 0.8 + 10) + '%';
		const weekday = dateFormat(dateObject, 'eee');
		const weekdayShort = dateFormat(dateObject, 'eeeeee');
		const dateNum = dateGetDate(dateObject);

		let styles = 'abPriceCutter__day';

		if (dateIsToday(dateObject) || dateIsBefore(dateObject, this.today) || dateIsAfter(dateObject, this.lastSelectableDay)) {
			styles += ' abPriceCutter__day--disabled';
		}

		if (obj.bound === 'depart') {
			if (this.searchData.departDate === date) {
				styles += ' abPriceCutter__day--active';
			}
		} else {
			if (this.searchData.returnDate === date) {
				styles += ' abPriceCutter__day--active';
			}
		}

		if (this.holidays) {
			const isHoliday = this.holidays.has(date);

			if (isHoliday) {
				styles += ' abPriceCutter__day--holiday';
			}
		}

		return `
				<div class="${styles}"
					aria-label="${dateLong}"
					data-date="${date}"
					data-datelong="${dateLong}"
					data-price="${lc(obj.price)}"
					data-bound="${obj.bound}"
					data-percent="${percent}">
					<div class="abPriceCutter__bar" ${(update) ? `style="height:${percent}"` : ``}></div>
					<div class="abPriceCutter__date">
						<span class="abPriceCutter__dateday">
							<span class="hidden-md-up">${weekday}</span>
							<span class="hidden-md-down">${weekdayShort}</span>
						</span>
						<span class="abPriceCutter__datenum">${dateNum}</span>
					</div>
				</div>
			`;
	}

	scrollToActiveBars () {
		qsa('.abPriceCutter__day--active').forEach((element) => {
			element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
		});
	}

	setHolidays () {
		if (document.documentElement.lang !== 'sv') {
			return;
		}

		this.holidays = new Set();

		if (window.holidayData) {
			const hols = window.holidayData;

			for (const hol in hols) {
				if (hols.hasOwnProperty(hol)) {
					this.holidays.add(hol);
				}
			}
		}
	}

	animateBars () {
		const days = this.html.querySelectorAll('.abPriceCutter__day');

		days.forEach(function (element) {
			element.querySelector('.abPriceCutter__bar').style.height = element.dataset.percent;
		});
	}

	setPriceRanges () {
		const outbounds = this.outbounds;
		const inbounds = this.inbounds;
		const tmp_prices = [];

		for (let i = 0; i < outbounds.length; i++) {
			tmp_prices.push(parseInt(outbounds[i].price));

			if (this.returnTrip) {
				tmp_prices.push(parseInt(inbounds[i].price));
			}
		}

		this.maxPrice = parseInt(Math.ceil(Math.max.apply(null, tmp_prices)));
		this.minPrice = parseInt(Math.ceil(Math.min.apply(null, tmp_prices)));
	}

	updatePriceRanges () {
		const outbounds = this.outbounds;
		const inbounds = this.inbounds;
		const tmp_prices = [];
		const out_prices = [];
		const in_prices = [];
		const prices = { outbounds: [], inbounds: [] };

		for (let i = 0; i < outbounds.length; i++) {
			out_prices.push(parseInt(outbounds[i].price));
			tmp_prices.push(parseInt(outbounds[i].price));

			if (this.returnTrip) {
				in_prices.push(parseInt(inbounds[i].price));
				tmp_prices.push(parseInt(inbounds[i].price));
			}
		}

		prices.outbounds = out_prices;
		prices.inbounds = in_prices;

		this.maxPrice = parseInt(Math.ceil(Math.max.apply(null, tmp_prices)));
		this.minPrice = parseInt(Math.ceil(Math.min.apply(null, tmp_prices)));

		return prices;
	}

	updateBarHeight (prices, min, max) {
		const outboudDates = this.departDates.map(date => dateFormatYMD(new Date(date)));

		for (let i = 0; i < outboudDates.length; i++) {
			const day = this.html.querySelector(`.abPriceCutter__graph--depart .abPriceCutter__day[data-date="${outboudDates[i]}"]`);
			if (!day) {
				continue;
			}
			const bar = day.querySelector('.abPriceCutter__bar');
			// const popup = bar.querySelector('.abPriceCutter__popup');
			const price = prices.outbounds[i];

			const percent = ((Math.ceil(((price - min) * 100) / ((max - min) * 100) * 100)) * 0.8 + 10) + '%';

			bar.style.height = percent;
			// popup.innerHTML = lc(price);
		}

		if (this.returnTrip) {
			const inboundDates = this.returnDates.map(date => dateFormatYMD(new Date(date)));

			for (let i = 0; i < inboundDates.length; i++) {
				const day = this.html.querySelector(`.abPriceCutter__graph--return .abPriceCutter__day[data-date="${inboundDates[i]}"]`);
				if (!day) {
					continue;
				}
				const bar = day.querySelector('.abPriceCutter__bar');
				// const popup = bar.querySelector('.abPriceCutter__popup');
				const price = prices.inbounds[i];

				const percent = ((Math.ceil(((price - min) * 100) / ((max - min) * 100) * 100)) * 0.8 + 10) + '%';

				bar.style.height = percent;
				// popup.innerHTML = lc(price);
			}
		}
	}

	hideMedianLines () {
		qsa('.abPriceCutter__medianLine', this.html).forEach((element) => {
			element.style.opacity = 0;
		});
	}

	updateMedianLines () {
		const graphs = [];

		graphs.push(this.html.querySelector('.abPriceCutter__graph--depart'));

		if (this.returnTrip) {
			graphs.push(this.html.querySelector('.abPriceCutter__graph--return'));
		}

		graphs.forEach((graph) => {
			const bar = graph.querySelector('.abPriceCutter__day--active .abPriceCutter__bar');
			const line = graph.querySelector('.abPriceCutter__medianLine');
			const scroller = graph.querySelector('.abPriceCutter__scroller');
			const offset = parseInt(window.getComputedStyle(scroller).paddingBottom, 10);

			line.style.bottom = (bar.offsetHeight + offset) + 'px';
			line.style.opacity = 1;
		});
	}

	showEstimatedPrice () {
		const price = this.html.querySelector('.abPriceCutter__day--active').dataset.price;
		const text = this.returnTrip ? l('module.pricecutter.estimatedPriceReturn') : l('module.pricecutter.estimatedPriceOneWay');

		this.html.querySelector('.abPriceCutter__estTxt').innerHTML = text;
		this.html.querySelector('.abPriceCutter__price').innerHTML = price;
		this.html.querySelector('.abPriceCutter__estimated').classList.add('abPriceCutter__estimated--visible');
	}

	hideEstimatedPrice () {
		this.html.querySelector('.abPriceCutter__estimated').classList.remove('abPriceCutter__estimated--visible');
	}

	updateFooter () {
		const selectedDates = document.querySelectorAll('.abPriceCutter__selectedDates');
		let mobileStr = `<p><span>${l('global.simplePhrases.departure')}: </span>${this.searchData.departDate}</p>`;
		let tabletStr = dateFormat(new Date(this.searchData.departDate), 'PPP');

		if (this.returnTrip) {
			mobileStr += `<p><span>${l('global.simplePhrases.return')}: </span>${this.searchData.returnDate}</p>`;
			tabletStr += ` - ${dateFormat(new Date(this.searchData.returnDate), 'PPP')}`;
		}

		selectedDates[0].children[0].innerText = tabletStr;
		selectedDates[1].innerHTML = mobileStr;

		qsa('button[data-action="search-price-cutter"]').forEach((element) => {
			showElement(element);
		});
		qsa('button[data-action="close-price-cutter"]').forEach((element) => {
			hideElement(element);
		});
	}

	events () {
		const html = this.html;

		let resizeTimer;

		window.addEventListener('resize', (e) => {
			if (!this.initialized) {
				return;
			}

			if (resizeTimer) {
				cancelAnimationFrame(resizeTimer);
			}

			resizeTimer = requestAnimationFrame(() => {
				this.datePricePopup.style.opacity = 0;
				this.scrollToActiveBars();
			});
		});

		html.addEventListener('click', (e) => {
			const elem = e.target;

			if (elem.closest('.abPriceCutter__day')) {
				const dayBar = elem.closest('.abPriceCutter__day');

				if (dayBar.classList.contains('abPriceCutter__day--active') || dayBar.classList.contains('abPriceCutter__day--disabled')) {
					// If day is already active
					return;
				} else {
					// If day is not disabled, update data
					const bound = dayBar.dataset.bound;
					const date = dayBar.dataset.date;

					if (bound === 'depart') {
						this.searchData.departDate = date;
					} else {
						this.searchData.returnDate = date;
					}

					this.selectedBound = bound;

					this.hideEstimatedPrice();
					this.updateEstimates();
					this.hideMedianLines();
					this.updateFooter();
				}

				[...dayBar.parentElement.children].forEach(day => day.classList.remove('abPriceCutter__day--active'));
				dayBar.classList.add('abPriceCutter__day--active');

				setTimeout(() => {
					this.scrollToActiveBars();
					this.updateMedianLines();
				}, 1000);
			}
		}, false);

		html.addEventListener('mouseover', (e) => {
			const elem = e.target;

			if (elem.closest('.abPriceCutter__bar')) {
				const { left, top } = elem.closest('.abPriceCutter__bar').getBoundingClientRect();
				const { datelong, price } = elem.closest('.abPriceCutter__day').dataset;

				this.showDatePricePopup({
					datelong,
					price,
					position: {
						left: left + 'px',
						top: top + 'px'
					}
				});
			}
		});

		html.addEventListener('mouseout', (e) => {
			const elem = e.target;

			if (elem.closest('.abPriceCutter__bar')) {
				this.datePricePopup.style.opacity = 0;
			}
		});

		document.addEventListener('click', (e) => {
			const elem = e.target;

			if (elem.matches('button')) {
				const action = elem.dataset.action;

				if (action === 'search-price-cutter') {
					e.stopPropagation();
					this.search();
					return;
				}

				if (action === 'close-price-cutter') {
					this.close();
				}
			}
		});
	}

	getSearchParameters () {
		let direct = 0;
		let nearbyDates = 0;
		let baggage = 0;
		const userSavedSearch = storedItemRead('userSavedSearch');

		if (userSavedSearch) {
			direct = parseInt(userSavedSearch.direct, 10);
			baggage = parseInt(userSavedSearch.baggage, 10);
			nearbyDates = parseInt(userSavedSearch.nearbyDates, 10);
		}

		const cabin = parseInt(js_params.classType, 10);
		const legs = JSON.parse(JSON.stringify(js_params.showresultSearchParams));

		let passengers = {};
		// Format passenger data
		if ('youthAges' in js_params && js_params.youthAges.length > 0) {
			passengers = {
				youth: {
					count: js_params.passengers,
					ages: js_params?.youthAges || []
				}
			};
		} else {
			passengers = {
				adults: js_params.adults,
				child: {
					count: js_params.children,
					ages: js_params.childrenAges
				}
			};
		}

		const searchParams = {
			tripType: js_params.returntrip,
			direct,
			baggage,
			nearbyDates,
			cabin,
			legs,
			passengers
		};

		return searchParams;
	}

	updateSearchParameters () {
		this.searchParams.legs[0].departDate = this.searchData.departDate;
		this.searchParams.legs[0].returnDate = this.searchData.returnDate;
	}

	showDatePricePopup (opts) {
		this.datePricePopup.innerHTML = `
				<div>
					<span>${opts.datelong}</span>
				</div>
				<div class="price">
					<span>${opts.price}</span>
				</div>
			`;

		this.datePricePopup.style.left = opts.position.left;
		this.datePricePopup.style.top = opts.position.top;
		this.datePricePopup.style.opacity = 1;
	}

	updateEstimates () {
		const values = Object.values(this.searchData);

		this.request = ConnectApi('PriceCutterAPI', 'getEstimatedPrices', values).then(response => this.parseEstimates(response));
	}

	close () {
		this.dateSelector.hide();
		this.dateSelector.toggleViewType('pricecalendar');
	}

	search () {
		this.dateSelector.showLoader();

		this.updateSearchParameters();

		const postData = this.getPostData();
		this.initiateSearch(postData);
	}

	initiateSearch (postData) {
		const handleError = () => {
			console.log('there was an error, dumping response args');
			// TODO: arguments is a reserved word in JS...
			console.log(arguments);

			this.cancelSearch();
			window.alert(l('error.e9'));
		};


		logEvent(events.cuttersearch, eventCategories.search);

		this.request = ConnectApi('Search', 'initiateSearch', [postData]).then((resp) => {
			const response = resp.results;
			const errors = resp.error;
			const { direct, baggage } = this.searchParams;

			if (errors) {
				errors.forEach(error => window.alert(error));
				return;
			}

			if (typeof response.id !== 'undefined') {
				const filterParams = {};

				if (direct === 1) {
					filterParams.numStops = 0;
				}

				if (baggage === 1) {
					filterParams.baggageIncluded = 1;
				}

				this.saveSearchToLocalStorage(this.searchParams);

				location.href = this.getShowresultUrl(response.id, filterParams);
			}
		}).catch(handleError);
	}

	getPostData () {
		const legs = this.searchParams.legs[0];
		const postData = {
			returntrip: this.searchParams.tripType,
			classType: this.searchParams.cabin,
			passengers: this.searchParams.passengers,
			trips: [
				{
					from: [{
						iata: legs.fromIata,
						isMetro: legs.fromMetro
					}],
					to: [{
						iata: legs.toIata,
						isMetro: legs.toMetro
					}],
					date: legs.departDate
				}
			]
		};

		if ([1, 2].includes(js_params.returntrip)) {
			postData.trips.push({
				from: [{
					iata: legs.toIata,
					isMetro: legs.toMetro
				}],
				to: [{
					iata: legs.fromIata,
					isMetro: legs.fromMetro
				}],
				date: legs.returnDate
			});
		}

		return postData;
	}

	saveSearchToLocalStorage (data = null) {
		if (!data || data === {}) {
			console.log('Tried to save empty user search');
			return;
		}
		storedItemCreate('userSavedSearch', data);
	}

	getShowresultUrl (id, filterParams) {
		let targetUrl = `/${lLink('global.navigation.resultsLink')}?id=${id}`;

		if (Object.keys(filterParams).length) {
			targetUrl += `&filter=${Base64.encode(JSON.stringify(filterParams))}`;
		}

		return targetUrl;
	}

	cancelSearch () {
		this.isSearching = false;
		this.request.abort();
		this.dateSelector.hideLoader();
	}
}
